import { useState } from "react";
import type { Business } from "@cliqsuite/db";
import { Chips, Field, Modal } from "./Modal";
import { subscriptionStatusMeta } from "../lib/format";
import type { Plan } from "../lib/adminTypes";
import type { SubscriptionFormValue } from "../state/AdminDataContext";

const STATUS_OPTIONS: Business["subscription_status"][] = ["active", "trial", "expired", "suspended", "inactive"];

/**
 * Manual override for a business's plan, status and dates, for corrections, extensions and comps
 * that don't go through a submitted payment.
 */
export function EditSubscriptionModal({
  business,
  plans,
  onClose,
  onConfirm,
}: {
  business: Business;
  plans: Plan[];
  onClose: () => void;
  onConfirm: (form: SubscriptionFormValue, planKey: string | null) => void;
}) {
  const currentPlan = plans.find((p) => p.id === business.subscription_plan_id);
  const [status, setStatus] = useState<Business["subscription_status"]>(business.subscription_status);
  const [planKey, setPlanKey] = useState<string>(currentPlan?.key ?? "");
  const [periodEnd, setPeriodEnd] = useState(business.subscription_period_end ?? "");
  const [graceEnd, setGraceEnd] = useState(business.grace_period_ends_at ?? "");
  const [reason, setReason] = useState(business.suspension_reason ?? "");

  const submit = () => {
    // The reason only means something while suspended; any other status clears it.
    onConfirm({ status, period_end: periodEnd || null, grace_period_ends_at: graceEnd || null, suspension_reason: status === "suspended" ? reason.trim() || null : null }, planKey || null);
    onClose();
  };

  return (
    <Modal
      onClose={onClose}
      width={500}
      title="Edit subscription"
      sub={`${business.name} · ${currentPlan?.name ?? "No plan"}`}
      footer={
        <>
          <button type="button" onClick={onClose} className="o-btn o-btn--grey">
            Cancel
          </button>
          <button type="button" onClick={submit} className="o-btn o-btn--ink">
            <span className="ms" aria-hidden="true">check</span>
            Save subscription
          </button>
        </>
      }
    >
      <Chips label="Status" value={status} onChange={setStatus} options={STATUS_OPTIONS.map((s) => ({ value: s, label: subscriptionStatusMeta(s).label }))} />
      {status === "suspended" && (
        <Field label="Reason, shown to the owner">
          <input className="o-input" aria-label="Reason, shown to the owner" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional, like a payment rejected twice" maxLength={200} />
        </Field>
      )}
      <Chips label="Plan" value={planKey} onChange={setPlanKey} options={plans.map((p) => ({ value: p.key, label: p.name }))} />
      <div className="o-fields2">
        <Field label="Plan period ends">
          <input className="o-input" aria-label="Plan period ends" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </Field>
        <Field label="Grace period ends">
          <input className="o-input" aria-label="Grace period ends" type="date" value={graceEnd} onChange={(e) => setGraceEnd(e.target.value)} />
        </Field>
      </div>
      <div className="o-note o-note--warn" style={{ marginTop: 0 }}>
        <span className="ms" aria-hidden="true">warning</span>
        <span>During grace the business keeps working. After it, registers and kitchen screens lock.</span>
      </div>
    </Modal>
  );
}
